/* eslint-disable */
import clsx from 'clsx';
import PropTypes, { string } from 'prop-types';
import React, { useState, useContext, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Grid, Button, Tooltip } from '@mui/material';
import { AppContext, CalculatorsContext, DraftContext, ErrorContext } from '../../../context';
import { answerStateSet, updateCalcAnswer } from '../../../api/rest/list';

import Form from '../../../components/form/Form';

import styles from './index.module.scss';
import CustomCo2Item from './components/CustomCo2Item/CustomCo2Item';

import { InfoIcon, HelpIcon, AddIcon } from 'tt-ui-kit';
import { CALC_MODELS } from '../../../constants';
import FileUploader from '../../../components/fileUploader/FileUploader';
import useDebounce from '../../../utils/useDebounce';
import QuestionSharingWrapper from '../../../components/questionSharingWrapper/QuestionSharingWrapper';

const tooltipText = `Please add the components used in this project.
For each component, specify the amount you need.
Select supplier for a component from the list or add them manually. Invite the supplier to the platform to confirm their participation in your project. This will provide greater transparency for your project.

Carbon Footprint (Optional):
If you have information about a component's carbon footprint (environmental impact), you can provide it here.

If you're unsure about any details, feel free to invite colleagues or teammates to collaborate by clicking "Invite to answer."
`;

const CustomCo2 = ({ nextRoute, setNextRoute, setChartPartData, currentStep }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { idx } = useParams();
  const [isItemsComponents, SetIsItemsComponents] = useState(false);
  const [scopeIndex, setScopeIndex] = useState(null);

  const { draftId } = useContext(DraftContext);
  const {
    customCo2Answers,
    setAnswersData,
    calcSteps,
    chartData,
    setDirtyAnswersMap,
    addCustomCo2Items,
    addCustomCo2Suppliers,
    setCustomCo2Answers,
    setCustomCo2Activity,
  } = useContext(CalculatorsContext);
  const { isProduction } = useContext(AppContext);

  const initNewActivity = () => ({
    id: 0,
    item: null,
    quantity: null,
    supplier: null,
    footprint: null,
    isOffset: false,
  });

  const activities = [...(customCo2Answers?.answers?.[scopeIndex] ?? [])];
  const itemComponents = [...(customCo2Answers?.items ?? [])];
  const suppliers = [...(customCo2Answers?.suppliers ?? [])];

  const debounce = useDebounce((data) => {
    setDirtyAnswersMap(data); // Передаём валидное значение
  }, 1000);

  const onSaveAnswer = () => {
    debounce({ [CALC_MODELS.CUSTOM_CO2]: true });
  };

  const isKeyFilled = (activity, key) => {
    if (!activity) return false;
    switch (key) {
      case 'item':
        return !!activity.item && !!activity.item?.id;
      case 'quantity':
        return !!activity.quantity && !isNaN(activity.quantity);
      case 'supplier':
        return !!activity.supplier && !!activity.supplier?.id;
      case 'footprint':
        return !!activity.footprint && !isNaN(activity.footprint);
      default:
        return true;
    }
  };

  const changeActivity = (value, fieldName, indx) => {
    const newActivities = activities.map((activity, index) => {
      if (index === indx) {
        const newActivity = {
          ...activity,
          [fieldName]: value ?? null,
        };
        return {
          ...newActivity,
          filled: Object.keys(newActivity).every((key) => isKeyFilled(newActivity, key)),
        };
      }
      return activity;
    });
    return newActivities;
  };

  const changeCustomCo2Data = (value, fieldName, indx, isNew = false) => {
    if (isNew && fieldName == 'item') {
      addCustomCo2Items(value);
    }
    if (isNew && fieldName == 'supplier') {
      addCustomCo2Suppliers(value);
    }
    const newActivities = changeActivity(value, fieldName, indx);
    setCustomCo2Activity(scopeIndex, newActivities);
    if (newActivities[indx]?.filled) onSaveAnswer();
  };

  const addAnswers = (newActivities) => {
    setCustomCo2Activity(scopeIndex, newActivities);
  };

  useEffect(() => {
    if (!customCo2Answers || scopeIndex == null) return;
    if (!customCo2Answers?.answers?.[scopeIndex]?.length) addAnswers([initNewActivity()]);
  }, [customCo2Answers, scopeIndex]);

  useEffect(() => {
    if (!idx) return;
    const newScopeIndex = calcSteps[idx - 1]?.scopeIndx;
    setScopeIndex(newScopeIndex);
    SetIsItemsComponents(true);
  }, [idx]);

  useEffect(() => {
    //chartData
    if (!chartData?.length || scopeIndex == null) return;
    const questionChartData = chartData[scopeIndex].children.find(
      (item) => item.type == CALC_MODELS.CUSTOM_CO2
    );
    setChartPartData([questionChartData]);
  }, [chartData, scopeIndex]);

  const onAddClick = () => addAnswers([...activities, initNewActivity()]);

  return (
    <>
      <div className={styles.formContainer}>
        <Form
          headerContent={
            <div className={styles.headerWrapper}>
              <div className={clsx(styles.headerContainer, styles.companyHeaderContainer)}>
                <p>Items/Components</p>
                <span>
                  <Tooltip tooltipText={tooltipText} enterTouchDelay={0}>
                    <InfoIcon className={clsx(styles.infoIcon, styles.topIcon)} />
                  </Tooltip>
                </span>
              </div>
              {!isProduction && (
                <div className={styles.inviteBlock}>
                  <QuestionSharingWrapper
                    model={CALC_MODELS.CUSTOM_CO2}
                    categoryCode={`${CALC_MODELS.CUSTOM_CO2}_${scopeIndex}`}
                    questionId={currentStep.id}
                  />
                  {/* <HelpIcon className={styles.helpIcon} /> */}
                </div>
              )}
            </div>
          }
        >
          {activities &&
            activities.map((activity, indx) => (
              <CustomCo2Item
                index={indx}
                onChange={(value, fieldName, isNew = false) =>
                  changeCustomCo2Data(value, fieldName, indx, isNew)
                }
                activity={activity}
                suppliers={suppliers}
                itemComponents={itemComponents}
              />
            ))}
          <div className={styles.addBlock}>
            {scopeIndex >= 0 && (
              <FileUploader questionCode={`scope${scopeIndex}`} model={CALC_MODELS.CUSTOM_CO2} />
            )}
            <div>
              <Button variant="outlined" className={styles.addButton} onClick={onAddClick}>
                <AddIcon className={styles.addIcon} />
                Add component
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </>
  );
};

export default CustomCo2;
